/** @jsx isolateComponent */

import { css } from '@emotion/react';
import type { ReactNode } from 'react';
import { CARET_SIZE } from './constants';

import {
  Animation,
  Colors,
  Icon,
  Link,
  Typography,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { emitUserInteractionEvent } from '../../utils/avroEvents';

import type { UniversalNavLinkType } from '../../constants';
import type { NavItem } from '../../constants/links';
import { UNIVERSAL_TOPBAR_HEIGHT } from '../../constants';
import { useUniversalNavContext } from '../../hooks';
import { MegaMenu } from './MegaMenu';

type Props = {
  /**
   * The menu that the link is tied to, for accessibility
   */
  ariaControls?: UniversalNavLinkType['text'];
  /**
   * Content to render within the link.
   */
  children: ReactNode;
  /**
   * Whether or not the link's menu is active
   */
  isMenuOpen?: boolean;
  /**
   *  The item to be rendered
   */
  navigationItem: NavItem;
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
  /**
   * Determines spacing styles if link is located
   * in the accounts menu vs. the main menu
   */
  position?: 'navigation' | 'actions-menu';
  /**
   * Accessibility role for the list item, either menuitem or none
   */
  role?: string;
  /**
   * Function to toggle the menu open/closed
   */
  toggleMenu?: () => void;
};

const Styles = labelStyles('UniversalTopbarLink', {
  root: (isMenuOnly: boolean) =>
    css({
      // caret icons are absolutely positioned (they don't take up width).
      // This space figures in the 24px width of the icon
      // and the 18px space needed between items
      paddingRight: isMenuOnly ? `${CARET_SIZE + 18}px` : 18,
      display: 'flex',
      alignItems: 'center',
      color: Colors.neutral700,
      ...Typography.paragraph3,
      '&:hover, &:focus, &:focus-within': {
        fontWeight: 600,
      },
      '&[data-position="actions-menu"]': {
        paddingRight: 0,
      },
    }),

  link: (isMenuOnly: boolean) =>
    css({
      color: Colors.inherit,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: UNIVERSAL_TOPBAR_HEIGHT,
      lineHeight: `${UNIVERSAL_TOPBAR_HEIGHT}px`,
      ...Typography.base,
      cursor: 'pointer',
      textDecoration: 'none',
      position: 'relative',

      '&:visited': {
        color: 'inherit',
      },

      '&:focus': {
        outline: 'none',
      },
      // Focus backgroundColor defined on `linkInner`
      '&:focus-within > span': {
        '&::before': {
          backgroundColor: Colors.neutral300,
        },
      },
      // Design wants a bold state on hover
      // this can be problematic for content shifting
      // when elements are in a row
      // Approach was recommended here:
      // https://css-tricks.com/bold-on-hover-without-the-layout-shift/
      '&::after': {
        content: 'attr(data-text)',
        height: 0,
        visibility: 'hidden',
        overflow: 'hidden',
        userSelect: 'none',
        pointerEvents: 'none',
        fontWeight: 600,
      },
      // Orange border underneath link that appears on selection
      '&::before': {
        content: '""',
        height: 6,
        width: 0,
        fontWeight: 600,
        position: 'absolute',
        color: Colors.zapier,
        borderBottom: '6px solid',
        bottom: 0,
        left: 0,
        zIndex: 10,
        transition: `width 200ms ${Animation.transitionTimingFunction}`,
      },
      '&[data-selected="true"]': {
        '&::before': {
          width: isMenuOnly ? 'calc(100% + 17px)' : '100%',
        },
      },
    }),
  linkInner: (isMenuOnly: boolean) =>
    // https://stackoverflow.com/questions/55780450/why-pseudo-element-background-color-covered-its-parent-background-color-when-use
    css({
      transformStyle: 'preserve-3d',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 20,
        right: isMenuOnly ? '-28px' : '-10px',
        bottom: 20,
        left: '-10px',
        transform: 'translateZ(-1px)',
        borderRadius: 4,
        height: 32,
      },
    }),
  caret: (isMenuOpen: boolean) => [
    css({
      transition: Animation.transitionValue,
      position: 'absolute',
      top: CARET_SIZE - 1,
      right: `-${CARET_SIZE}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: CARET_SIZE,
      width: CARET_SIZE,
    }),
    isMenuOpen &&
      withModifier('menu-open', {
        transform: 'rotate(-180deg)',
      }),
  ],
});

/**
 * @private
 * Wrapper that renders either a `Link` or forwards the `navigationItem` to `MegaMenu` depending on `navigationItem`'s `variant` prop.
 */
export const UniversalTopbarLink = ({
  ariaControls,
  children,
  isMenuOpen,
  navigationItem,
  onCloseMenu,
  position,
  role,
  toggleMenu,
}: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  //Remove spaces and special characters
  const sanitizedId = ariaControls?.replace(/[^\w]/g, '');

  const isMenuOnly = navigationItem.variant !== 'link';

  const handleEvent = (eventType: string) => {
    let eventMessage: string = '';
    if (eventType === 'click') {
      eventMessage = `${
        isMenuOpen ? 'Closing' : 'Opening'
      } UniversalLayout DynamicTopBar: ${
        isMenuOnly ? children : navigationItem.href
      }`;
    } else {
      eventMessage = `UniversalLayout DynamicTopBar: ${
        isMenuOnly ? children : navigationItem.href
      }`;
    }
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: eventMessage,
    });
  };

  const WrapperElement = position === 'actions-menu' ? 'span' : 'li';

  if (navigationItem.variant === 'link') {
    return (
      // WrapperElement code is repeated across both conditional blocks.
      // Extracting to a separate component caused all caret icons to flash & test failures.
      <WrapperElement
        css={Styles.root(false)}
        data-position={position}
        data-testid="topbar-link"
        role={role}
      >
        <Link
          color={null}
          css={Styles.link(false)}
          data-text={children}
          data-selected={false}
          href={getFullyQualifiedURL(navigationItem.href)}
          target={navigationItem.target}
          onClick={() => {
            handleEvent('click');
            if (toggleMenu && !navigationItem.href) toggleMenu();
          }}
          onMouseEnter={() => handleEvent('hover')}
          aria-controls={sanitizedId}
          aria-expanded={undefined}
        >
          <span css={Styles.linkInner(false)}>{children}</span>
        </Link>
      </WrapperElement>
    );
  } else {
    return (
      <WrapperElement
        css={Styles.root(true)}
        data-position={position}
        data-testid="topbar-link"
        role={role}
      >
        <button
          css={Styles.link(true)}
          data-text={children}
          data-selected={isMenuOpen}
          onClick={() => {
            handleEvent('click');
            if (toggleMenu) toggleMenu();
          }}
          onMouseEnter={() => handleEvent('hover')}
          aria-controls={sanitizedId}
          aria-expanded={isMenuOpen}
        >
          <span css={Styles.linkInner(true)}>
            {children}
            <span css={Styles.caret(isMenuOpen)}>
              <Icon isBlock={true} name="arrowSmallDown" size={16} />
            </span>
          </span>
        </button>
        <MegaMenu
          id={sanitizedId || ''}
          isMenuOpen={isMenuOpen || false}
          key={(isMenuOpen || 'false').toString()}
          navigationItem={navigationItem}
          onCloseMenu={onCloseMenu}
        />
      </WrapperElement>
    );
  }
};

export default UniversalTopbarLink;
